import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

// Friendly inline copy for each resolvePromoCode / session-state failure surfaced by the
// POST /api/v1/sessions/:id/apply-promo route. Keep keys in sync with the route's `error` body.
const ERROR_COPY: Record<string, string> = {
  promo_code_invalid: "That code isn't valid.",
  promo_code_expired: 'That promo code has expired.',
  promo_code_disabled: 'That promo code is no longer active.',
  promo_code_exhausted: 'That promo code has been fully redeemed.',
  session_not_payable: "This checkout can't accept a code right now.",
  session_expired: 'This checkout link has expired.',
};

function mapApplyError(message: string | undefined): string {
  if (!message) return 'Could not apply promo code';
  return ERROR_COPY[message] ?? "That code isn't valid.";
}

export interface PromoCodeApplyBoxProps {
  onApply: (code: string) => Promise<{ ok: true } | { ok: false; errorCode?: string }>;
}

export const PromoCodeApplyBox: React.FC<PromoCodeApplyBoxProps> = ({ onApply }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    const raw = code.trim();
    if (!raw) {
      setError(mapApplyError('promo_code_invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await onApply(raw);
      if (!result.ok) {
        setError(mapApplyError(result.errorCode));
      }
    } catch {
      setError(mapApplyError(undefined));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className='flex flex-col gap-2 rounded-lg border p-3'
      style={{ borderColor: 'var(--pp-border)', background: 'var(--pp-subtle)' }}
      data-testid='promo-apply-box'
    >
      <label
        htmlFor='promo-code-input'
        className='text-xs font-medium'
        style={{ color: 'var(--pp-text-muted)' }}
      >
        Have a promo code?
      </label>
      <div className='flex gap-2'>
        <input
          id='promo-code-input'
          name='promo-code'
          type='text'
          autoComplete='off'
          inputMode='text'
          spellCheck={false}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          disabled={submitting}
          placeholder='Enter code'
          aria-invalid={!!error}
          aria-describedby={error ? 'promo-code-error' : undefined}
          className='flex-1 rounded-md border px-3 py-2 text-sm disabled:opacity-60 focus:outline-none focus:ring-2'
          style={{
            background: 'var(--pp-surface)',
            borderColor: error ? 'var(--pp-warning)' : 'var(--pp-border)',
            color: 'var(--pp-text)',
            fontFamily: 'var(--pp-mono)',
          }}
          data-testid='promo-code-input'
        />
        <button
          type='submit'
          disabled={submitting || !code.trim()}
          className='inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold disabled:opacity-60'
          style={{ background: 'var(--pp-action)', color: '#fff' }}
          data-testid='promo-code-apply-button'
        >
          {submitting ? <Loader2 size={14} className='animate-spin' /> : null}
          Apply
        </button>
      </div>
      <div
        id='promo-code-error'
        aria-live='polite'
        role='status'
        className='min-h-[1rem] text-xs'
        style={{ color: 'var(--pp-warning)' }}
        data-testid='promo-code-error'
      >
        {error ?? ''}
      </div>
    </form>
  );
};

export default PromoCodeApplyBox;
